import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { isPresent } from '@ember/utils'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { on } from '@ember/modifier'
import { restartableTask, timeout } from 'ember-concurrency'
import type Store from '@ember-data/store'
import type { IntlService } from 'ember-intl'
import type BookLibraryBook from 'teacher-ui-client/models/book-library/book'
import { t } from 'ember-intl'
import { SearchBox } from 'teacher-ui-client/components/search-box'
import { RadioGroup } from 'teacher-ui-client/components/base/radio-group'
import { CheckboxGroup } from 'teacher-ui-client/components/base/checkbox-group'
import type { AssignmentOption } from 'teacher-ui-client/components/assignments-admin/assignment-form'
import type { Chapter } from 'teacher-ui-client/models/book-library/book-metadata'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import { OnChange } from 'teacher-ui-client/modifiers/on-change'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import type Owner from '@ember/owner'
import type BookLibraryCollectionService from 'teacher-ui-client/services/book-library/collection'
import { BookModal } from 'teacher-ui-client/components/book-library/book-modal'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type SessionService from 'teacher-ui-client/services/session'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'

function quizzableChapters(chapters: Chapter[]): Chapter[] {
  return chapters.filter((chapter) => chapter.has_quiz)
}

function isQuizzableBook(book: BookLibraryBook): boolean {
  return book.hasQuiz || quizzableChapters(book.chapters).length > 0
}

function firstItem<T>(arr: T[]): T | undefined {
  return arr[0]
}

export interface ChapterOption {
  id: number
  label: string
}

interface Signature {
  Args: {
    isReadAloud?: boolean
    selectAssignmentAction: (option?: AssignmentOption) => void
    selectedAssignmentOption?: AssignmentOption
  }
}

/**
 * A component that displays a book list dropdown and chapter checkboxes for a selected book
 */
export class ActivitySelectorBookList extends Component<Signature> {
  @tracked searchValue?: Nullable<string> = ''

  @service store!: Store

  @service intl!: IntlService

  @queryParam('assignments/product/create') bookCode?: BookLibraryBook['code']

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args)
    this.preassignBook()
  }

  preassignBook() {
    if (this.bookCode) {
      const book = this.store.peekAll<BookLibraryBook>('book-library/book').find((book) => book.code === this.bookCode)

      if (!book) return

      this.searchValue = book.bookMetadata.name
      this.selectBook(book)
    }
  }

  get selectedBook() {
    // If there's a selected book, then it must have been loaded, peek it
    const { selectedAssignmentOption } = this.args
    return (
      selectedAssignmentOption &&
      this.store.peekAll<BookLibraryBook>('book-library/book').find((book) => book.code === selectedAssignmentOption.id)
    )
  }

  get selectedChapterOptionIds(): ChapterOption['id'][] {
    return this.args.selectedAssignmentOption?.assignmentData.chapters ?? []
  }

  /**
   * Apply an id and name to chapters.
   * If the first chapter of the book starts with 'Chapter', we don't need to apply the prefix.
   */
  toChapterOptions(chapters: Chapter[]) {
    const noPrefix = chapters.some((chapter) => chapter.name?.startsWith('Chapter'))
    return chapters.map<ChapterOption>((chapter, index) => {
      const id = index + 1
      const label = noPrefix
        ? chapter.name
        : this.intl.t('assignments.create.books.chapterName', { id, name: chapter.name })
      return { id, label }
    }, [])
  }

  get assignableChaptersOfSelectedBook() {
    const chapters = this.selectedBook?.chapters ?? []
    return this.args.isReadAloud ? chapters : quizzableChapters(chapters)
  }

  get chapterOptionSet() {
    return {
      id: 'chapter-option-set',
      legend: this.args.isReadAloud
        ? this.intl.t('assignments.create.books.selectChapter')
        : this.intl.t('assignments.create.books.selectChapters'),
      options: this.toChapterOptions(this.assignableChaptersOfSelectedBook),
    }
  }

  searchBooks = restartableTask(async (searchValue: string) => {
    await timeout(500)

    const query = {
      filter: {
        excluded: false,
        name: searchValue,
      },
      page: {
        number: 1,
        size: 100,
      },
    }

    const books = await this.store.query<BookLibraryBook>('book-library/book', query)

    // read aloud assignments don't need a quiz to be present
    return this.args.isReadAloud ? books : books.filter(isQuizzableBook)
  })

  /**
   * Sets the selected book in the component, and sends the model to the `selectActivityAction`
   * in the parent component (activity-selector)
   * @memberOf  BookListComponent
   * @instance
   * @param  {BookModel} book A selected book model
   */
  @action
  selectBook(book: Nullable<BookLibraryBook>) {
    if (!book) {
      this.args.selectAssignmentAction(undefined)
      return
    }

    const bookInfo = {
      book_name: book.bookMetadata.name,
      lexile: book.bookMetadata.lexileLevel,
      classification: book.classification,
    }

    if (this.args.isReadAloud && book.chapters.length) {
      this.args.selectAssignmentAction({
        id: book.code,
        label: book.bookMetadata.name,
        book,
        assignmentData: {
          assignment_id: book.code,
          assignment_type: 'read_aloud_chapter',
          chapters: [1], // pre-select first chapter
          ...bookInfo,
        },
      })
      return
    }

    if (this.args.isReadAloud && !book.chapters.length) {
      this.args.selectAssignmentAction({
        id: book.code,
        label: book.bookMetadata.name,
        book,
        assignmentData: {
          assignment_id: book.code,
          assignment_type: 'read_aloud_book',
          ...bookInfo,
        },
      })
      return
    }

    const chapters = quizzableChapters(book.chapters)

    if (!this.args.isReadAloud && chapters.length) {
      this.args.selectAssignmentAction({
        id: book.code,
        label: book.bookMetadata.name,
        book,
        assignmentData: {
          assignment_id: book.code,
          assignment_type: 'chapter',
          chapters: this.toChapterOptions(chapters).map((opt) => opt.id), // pre-select all chapters
          ...bookInfo,
        },
      })
      return
    }

    if (!this.args.isReadAloud && !chapters.length) {
      this.args.selectAssignmentAction({
        id: book.code,
        label: book.bookMetadata.name,
        book,
        assignmentData: {
          assignment_id: book.code,
          assignment_type: 'book',
          ...bookInfo,
        },
      })
      return
    }
  }

  @action
  onUpdateChapter(optionSet: unknown, chapterId: ChapterOption['id']) {
    this.onUpdateChapters(optionSet, [chapterId])
  }

  @action
  onUpdateChapters(_optionSet: unknown, chapterIds: ChapterOption['id'][]) {
    const { selectedAssignmentOption } = this.args
    if (!selectedAssignmentOption) return

    // select exact same activity again, but with the desired chapters in the assignment payload
    this.args.selectAssignmentAction({
      ...selectedAssignmentOption,
      assignmentData: { ...selectedAssignmentOption.assignmentData, chapters: chapterIds },
    })
  }

  @action
  findBookByTitle(newSearchTerm: string | null | undefined) {
    this.searchValue = newSearchTerm

    if (isPresent(newSearchTerm)) {
      void this.searchBooks.perform(newSearchTerm)
    } else {
      // clear selected book if they reset search input
      this.selectBook(null)
    }
  }

  @action
  resetSearch() {
    this.searchValue = ''
  }

  <template>
    {{! Ensure the search value is reset if user switches between library book & read aloud }}
    <div {{OnChange this.resetSearch @isReadAloud}}>
      <div class="mb-4 px-3 md:w-full lg:w-1/2 lg:min-w-fit">
        <label class="mb-1">
          {{t "assignments.create.books.bookTitle"}}
        </label>
        <div class="flex gap-4">
          <SearchBox
            class="w-full"
            @value={{this.searchValue}}
            @placeholder={{t "assignments.create.books.bookSearchPlaceholder"}}
            @updateAction={{this.findBookByTitle}}
            @selectOptionAction={{this.selectBook}}
            @autocomplete={{true}}
            @autocompleteOptions={{this.searchBooks.lastSuccessful.value}}
            as |book|
          >
            <div class="space-y-3 font-normal">
              <p class="text-base text-black">
                [{{book.formattedSeries}}]&nbsp;
                {{book.title}}
              </p>
              <p class="text-dusty-black-200 text-base">
                {{t "assignments.create.books.lexiles" lexile=book.bookMetadata.lexileLevel}}
                &nbsp;
                {{t "assignments.create.books.age" age=book.bookMetadata.readingAge}}
              </p>
            </div>
          </SearchBox>
          {{#if this.selectedBook}}
            <BookPreviewButton data-test-book-preview class="whitespace-nowrap" @book={{this.selectedBook}} />
          {{/if}}
        </div>
      </div>
      {{#if this.chapterOptionSet.options}}
        {{#if @isReadAloud}}
          <RadioGroup
            data-test-book-chapter
            class="space-y-3 px-3 md:w-full lg:w-2/3"
            @optionSet={{this.chapterOptionSet}}
            @checkedOptionId={{firstItem this.selectedChapterOptionIds}}
            @onSelectionChange={{this.onUpdateChapter}}
          />
        {{else}}
          <CheckboxGroup
            data-test-book-chapters
            class="space-y-3 px-3 md:w-full lg:w-2/3"
            @optionSet={{this.chapterOptionSet}}
            @selectAllLabel={{t "assignments.allSelection"}}
            @checkedOptionIds={{this.selectedChapterOptionIds}}
            @onSelectionChange={{this.onUpdateChapters}}
          />
        {{/if}}
      {{/if}}
    </div>
  </template>
}

interface BookPreviewSignature {
  Element: HTMLElement
  Args: {
    book: BookLibraryBook
  }
}

class BookPreviewButton extends Component<BookPreviewSignature> {
  @service subscriptionType!: SubscriptionTypeService

  @service session!: SessionService

  @service('book-library/collection') collectionService!: BookLibraryCollectionService

  @tracked showBookModal = false

  get bookId() {
    return this.args.book.id ?? undefined
  }

  get isSubco() {
    return this.subscriptionType.current && this.session.currentTeacher.subcoFor(this.subscriptionType.current)
  }

  openBookModal = () => {
    this.showBookModal = true
  }

  closeBookModal = () => {
    this.showBookModal = false
  }

  <template>
    <UiButton {{on "click" this.openBookModal}} ...attributes>
      {{t "assignments.create.books.previewBook"}}<PhosphorIcon @icon="phosphor-arrow-square-out-bold" class="ml-2" />
    </UiButton>
    {{#if this.showBookModal}}
      <BookModal
        @bookId={{this.bookId}}
        @isSubco={{this.isSubco}}
        @collections={{this.collectionService.allMyCollections}}
        @showBookModal={{this.showBookModal}}
        @closeBookModal={{this.closeBookModal}}
        @theme={{themeKey this.subscriptionType.current}}
      />
    {{/if}}
  </template>
}
